import React, { useMemo } from 'react';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { formatLength } from '../utils/geometry';

// Farben
const LOT_COLOR = '#06b6d4'; // Cyan für Lot-Linien
const CEILING_POINT_COLOR = '#ef4444'; // Rot für Deckenpunkte
const CEILING_LINE_COLOR = '#9333ea'; // Lila für Deckenlinien

/**
 * CeilingView3D - Visualisierung der Deckenebene
 *
 * Zeigt:
 * 1. Lot-Linien: Vertikale Linien von jedem Punkt (z=0) zur Deckenhöhe (z=point.z)
 * 2. Deckenpunkte: Punkte oben am Ende jedes Lots
 * 3. Deckenlinien: Verbindungen zwischen Deckenpunkten (wo Wände existieren)
 * 4. Raumdecken: Halbtransparente Flächen für jeden Raum auf Deckenhöhe
 */
export function CeilingView3D({
  points,
  walls,
  spaces,
  viewMode = '2d',
  showLots = true,
  showCeilingPoints = true,
  showCeilingLines = true,
  showCeilingFill = true,
  showHeightLabels = true,
  labelSize = 150,
}) {
  // Nur in 3D-Ansicht anzeigen
  if (viewMode !== '3d') return null;

  // Lot-Linien und Deckenpunkte für alle Punkte
  const lotElements = useMemo(() => {
    return points.map(point => {
      const height = point.z || 0;
      if (height <= 0) return null; // Keine Lot-Linie wenn Höhe 0

      return {
        id: point.id,
        x: point.x,
        y: point.y,
        height,
      };
    }).filter(Boolean);
  }, [points]);

  // Deckenlinien (obere Kanten der Wände)
  const ceilingLines = useMemo(() => {
    return walls.map(wall => {
      const startPoint = points.find(p => p.id === wall.startPointId);
      const endPoint = points.find(p => p.id === wall.endPointId);

      if (!startPoint || !endPoint) return null;

      const startHeight = startPoint.z || 0;
      const endHeight = endPoint.z || 0;

      return {
        id: wall.id,
        start: { x: startPoint.x, y: startPoint.y, z: startHeight },
        end: { x: endPoint.x, y: endPoint.y, z: endHeight },
      };
    }).filter(Boolean);
  }, [walls, points]);

  // Deckenpolygone für Räume
  const ceilingPolygons = useMemo(() => {
    return spaces.map(space => {
      const polygon = (space.pointIds || [])
        .map(pid => points.find(p => p.id === pid))
        .filter(Boolean);

      if (polygon.length < 3) return null;

      // Durchschnittliche Deckenhöhe für den Raum
      const avgHeight = polygon.reduce((sum, p) => sum + (p.z || 0), 0) / polygon.length;

      return {
        id: space.id,
        name: space.name,
        polygon,
        avgHeight,
        colorIndex: space.colorIndex || 0,
      };
    }).filter(Boolean);
  }, [spaces, points]);

  return (
    <group name="ceiling-view">
      {/* 1. Lot-Linien (vertikale Linien von Boden zur Decke) */}
      {showLots && lotElements.map(lot => (
        <group key={`lot-${lot.id}`}>
          {/* Vertikale Lot-Linie */}
          <line>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                count={2}
                array={new Float32Array([
                  lot.x, lot.y, 0,
                  lot.x, lot.y, lot.height,
                ])}
                itemSize={3}
              />
            </bufferGeometry>
            <lineBasicMaterial color={LOT_COLOR} linewidth={2} />
          </line>

          {/* Gestrichelte Hilfslinien (optional: horizontale Linien zur besseren Orientierung) */}
          {/* Kleine Markierungen am Boden und an der Decke */}
          <mesh position={[lot.x, lot.y, 0]}>
            <ringGeometry args={[40, 60, 16]} />
            <meshBasicMaterial color={LOT_COLOR} side={THREE.DoubleSide} />
          </mesh>
        </group>
      ))}

      {/* 2. Deckenpunkte (rote Kugeln oben) */}
      {showCeilingPoints && lotElements.map(lot => (
        <group key={`ceiling-point-${lot.id}`}>
          <mesh position={[lot.x, lot.y, lot.height]}>
            <sphereGeometry args={[60, 16, 16]} />
            <meshBasicMaterial color={CEILING_POINT_COLOR} />
          </mesh>

          {/* Höhenbeschriftung */}
          {showHeightLabels && (
            <Text
              position={[lot.x + 150, lot.y, lot.height + 100]}
              fontSize={labelSize}
              color={CEILING_POINT_COLOR}
              anchorX="left"
              anchorY="middle"
            >
              {formatLength(lot.height)} m
            </Text>
          )}
        </group>
      ))}

      {/* 3. Deckenlinien (Verbindungen oben) */}
      {showCeilingLines && ceilingLines.map(line => (
        <line key={`ceiling-line-${line.id}`}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={2}
              array={new Float32Array([
                line.start.x, line.start.y, line.start.z,
                line.end.x, line.end.y, line.end.z,
              ])}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial color={CEILING_LINE_COLOR} linewidth={3} />
        </line>
      ))}

      {/* 4. Deckenpolygone (halbtransparente Flächen) */}
      {showCeilingFill && ceilingPolygons.map(ceiling => (
        <CeilingPolygon
          key={`ceiling-polygon-${ceiling.id}`}
          polygon={ceiling.polygon}
          avgHeight={ceiling.avgHeight}
          name={ceiling.name}
          colorIndex={ceiling.colorIndex}
          showLabel={true}
          labelSize={labelSize}
        />
      ))}
    </group>
  );
}

// Einzelnes Deckenpolygon - mit echten 3D-Koordinaten (jeder Punkt hat seine eigene Z-Höhe)
function CeilingPolygon({ polygon, avgHeight, name, colorIndex, showLabel, labelSize }) {
  // 3D BufferGeometry mit tatsächlichen Punkt-Höhen erstellen
  const geometry = useMemo(() => {
    if (polygon.length < 3) return null;

    // Vertices mit echten Z-Koordinaten
    const vertices = polygon.map(p => [p.x, p.y, p.z || 0]);

    // Fan-Triangulation vom ersten Vertex aus
    // Für ein Polygon mit n Vertices: (n-2) Dreiecke
    const indices = [];
    for (let i = 1; i < polygon.length - 1; i++) {
      indices.push(0, i, i + 1);
    }

    // BufferGeometry erstellen
    const geom = new THREE.BufferGeometry();

    // Position Buffer
    const positions = new Float32Array(vertices.flat());
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Index Buffer für Triangulation
    geom.setIndex(indices);

    // Normalen berechnen für korrekte Beleuchtung
    geom.computeVertexNormals();

    return geom;
  }, [polygon]);

  // Umrandung mit echten Z-Koordinaten
  const borderPositions = useMemo(() => {
    if (polygon.length < 3) return null;
    // Jeder Punkt mit seiner echten Z-Höhe + kleiner Offset
    return new Float32Array(polygon.flatMap(p => [p.x, p.y, (p.z || 0) + 5]));
  }, [polygon]);

  // Schwerpunkt für Label berechnen (mit Durchschnitts-Z für Label-Position)
  const centroid = useMemo(() => {
    if (polygon.length === 0) return { x: 0, y: 0, z: 0 };
    const sumX = polygon.reduce((sum, p) => sum + p.x, 0);
    const sumY = polygon.reduce((sum, p) => sum + p.y, 0);
    const sumZ = polygon.reduce((sum, p) => sum + (p.z || 0), 0);
    return {
      x: sumX / polygon.length,
      y: sumY / polygon.length,
      z: sumZ / polygon.length,
    };
  }, [polygon]);

  // Farben für verschiedene Räume
  const colors = [
    '#3b82f6', '#22c55e', '#f59e0b', '#ef4444',
    '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6',
  ];
  const color = colors[colorIndex % colors.length];

  if (!geometry || !borderPositions) return null;

  return (
    <group>
      {/* Deckenfläche mit echten 3D-Koordinaten */}
      <mesh geometry={geometry}>
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.25}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Umrandung der Decke mit echten Z-Höhen */}
      <lineLoop>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={polygon.length}
            array={borderPositions}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} linewidth={2} />
      </lineLoop>

      {/* Raum-Label auf Durchschnitts-Deckenhöhe */}
      {showLabel && name && (
        <Text
          position={[centroid.x, centroid.y, centroid.z + 150]}
          fontSize={labelSize * 1.5}
          color={color}
          anchorX="center"
          anchorY="middle"
          fontWeight="bold"
        >
          {name}
        </Text>
      )}
    </group>
  );
}
